import { X } from 'lucide-react';
import { FormInput, PhoneInput } from '@/components';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import { LoginFormData } from '@/components/sign-up/sign-up.types.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import { schema } from './login-form.schema.ts';
import { useState, useEffect } from 'react';
import { LoginFormTypes } from './login-form.types.ts';
import { useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import { useLogin } from '@/hooks/use-login.tsx';
import { parseJwt } from '@/utility';
import { TimeSpan } from '@/utility/time-span.ts';
import useDeviceInfo from '@/hooks/use-device-info.tsx';

const LoginForm = ({ onClose, onFormChange, showCloseButton = true }: LoginFormTypes) => {
	const { loginMutation, loginLoading } = useLogin();
	const { deviceRequest } = useDeviceInfo();

	const navigation = useNavigate();
	const [phoneError, setPhoneError] = useState<string | null>(null);
	const [passwordError, setPasswordError] = useState<string | null>(null);
	const [formReady, setFormReady] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
		setValue,
		watch,
		trigger
	} = useForm<LoginFormData>({
		resolver: yupResolver(schema),
		mode: 'onChange'
	});

	useEffect(() => {
		const timer = setTimeout(() => {
			const phoneInput = document.querySelector('input[name="phone"]');
			const passwordInput = document.querySelector('input[name="password"]');

			if (phoneInput instanceof HTMLInputElement && phoneInput.value) {
				setValue('phone', phoneInput.value, { shouldValidate: true });
			}

			if (passwordInput instanceof HTMLInputElement && passwordInput.value) {
				setValue('password', passwordInput.value, { shouldValidate: true });
			}

			if (phoneInput?.value && passwordInput?.value) {
				trigger();
				setFormReady(true);
			}
		}, TimeSpan.milliSecond.threeHundredFifty);

		return () => clearTimeout(timer);
	}, [setValue, trigger]);

	const isDisabledForm = loginLoading || (!isValid && formReady);

	const onSubmit = async (data: LoginFormData) => {
		try {
			loginMutation(
				{
					phoneNumber: data.phone.replace(/\D/g, ''),
					password: data.password,
					deviceRequest: deviceRequest
				},
				(data, errorData) => {
					if (errorData?.error === 'LE101' || errorData?.error === 'LE-USER401') {
						setPasswordError('Kullanıcı adı veya şifre hatalı.');
						return;
					}

					const isApplicant = parseJwt(data?.access_token)?.resource_access?.['live-expert-applicant']?.roles?.includes('Expert_Applicant');

					setTimeout(() => {
						navigation(isApplicant ? NavigationPath.applicationOnboarding : NavigationPath.coachAppointments);
					}, TimeSpan.milliSecond.threeHundred);

					setPhoneError(null);
					setPasswordError(null);
				}
			);
		} catch (error: any) {
			setPasswordError(error?.response?.data?.message || 'Giriş yapılırken bir hata oluştu');
			setPhoneError(null);
		}
	};

	const onNavigateForgetPassword = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		onFormChange('forget-password');
	};

	const handlePhoneChange = (value: string) => {
		setValue('phone', value, { shouldValidate: true });
		setPhoneError(null);
		setPasswordError(null);
	};

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValue('password', e.target.value, { shouldValidate: true });
		setPhoneError(null);
		setPasswordError(null);
	};

	return (
		<div className="login-form-container">
			<div className="login-modal-header">
				<h2>Giriş Yap</h2>
				<p>Giriş yapabilmek için telefon numaranızı ve şifrenizi girin.</p>
				{showCloseButton && (
					<button
						className="close-button"
						onClick={onClose}>
						<X size={20} />
					</button>
				)}
			</div>

			<form
				onSubmit={handleSubmit(onSubmit)}
				className="login-form">
				<PhoneInput
					value={watch('phone') || ''}
					onChange={handlePhoneChange}
					disabled={loginLoading}
					name="phone"
				/>
				{(errors.phone || phoneError) && (
					<div className="error-message">
						<i className="icons-info-circle" />
						{phoneError || errors.phone?.message}
					</div>
				)}

				<FormInput
					label="Şifre"
					variant="password"
					{...register('password')}
					value={watch('password') || ''}
					onChange={handlePasswordChange}
					placeholder="Şifrenizi yazın"
					autoComplete="on"
					error={Boolean(errors.password || passwordError)}
					disabled={loginLoading}
				/>
				<div className="error-wrapper">
					<a
						href="#"
						className="forgot-password"
						onClick={onNavigateForgetPassword}
						style={{ cursor: 'pointer' }}>
						Şifremi Unuttum
					</a>
					{(errors.password || passwordError) && (
						<div className="error-message">
							<i className="icons-info-circle" />
							{passwordError || errors.password?.message}
						</div>
					)}
				</div>

				<button
					type="submit"
					className={clsx('submit-button', { disabled: isDisabledForm })}
					disabled={isDisabledForm}>
					{loginLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
				</button>

				<div className="form-footer">
					<span className="text-muted">Henüz bir hesabın yok mu?</span>
					<a
						href="/create-account"
						className="link">
						Hesap Oluştur
					</a>
				</div>
			</form>
		</div>
	);
};

export default LoginForm;
